import { create } from 'zustand'
import type { CrowdWorker, CrowdWorkerStats } from '../types'
import { authenticateTelegram, devMockAuth } from '../api/auth'
import { getMyProfile, getMyStats, updatePreferences } from '../api/workers'
import { getTelegramInitData } from '../utils/telegram'

export const isMockMode = () =>
  new URLSearchParams(window.location.search).get('mock') === 'true'

interface AuthState {
  worker: CrowdWorker | null
  stats: CrowdWorkerStats | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  initAuth: () => Promise<void>
  fetchStats: () => Promise<void>
  setPreferredLanguage: (lang: string) => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  worker: null,
  stats: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  initAuth: async () => {
    const token = localStorage.getItem('crowd_access_token')
    if (token) {
      set({ isAuthenticated: true })
      // Fetch profile in background to restore preferred_language without blocking navigation
      getMyProfile().then((worker) => set({ worker })).catch(() => {})
      return
    }

    set({ isLoading: true, error: null })

    // Dev mock mode: call backend dev-mock endpoint
    if (isMockMode()) {
      try {
        const result = await devMockAuth()
        localStorage.setItem('crowd_access_token', result.access_token)
        set({ worker: result.worker, isAuthenticated: true, isLoading: false })
      } catch {
        set({ error: 'Mock auth failed — is DEBUG=True on backend?', isLoading: false })
      }
      return
    }

    const initData = getTelegramInitData()
    if (!initData) {
      set({ isLoading: false, isAuthenticated: false })
      return
    }

    try {
      const result = await authenticateTelegram(initData)
      localStorage.setItem('crowd_access_token', result.access_token)
      set({ worker: result.worker, isAuthenticated: true, isLoading: false })
    } catch {
      set({ error: 'Authentication failed', isLoading: false, isAuthenticated: false })
    }
  },

  fetchStats: async () => {
    if (!get().isAuthenticated) return
    try {
      const stats = await getMyStats()
      set({ stats })
    } catch {
      // Silently fail — stats are non-critical
    }
  },

  setPreferredLanguage: async (lang: string) => {
    try {
      const updatedWorker = await updatePreferences({ preferred_language: lang })
      set((state) => ({ worker: state.worker ? { ...state.worker, ...updatedWorker } : updatedWorker }))
    } catch {
      // silently fail
    }
  },

  logout: () => {
    localStorage.removeItem('crowd_access_token')
    set({ worker: null, stats: null, isAuthenticated: false })
  },
}))
